import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  TextField,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { FormEvent, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { OpenAiService } from "../../services/api";
import AskAiResponseDialog from "./AskAiResponseDialog";

interface AskAiDialogProps {
  open: boolean;
  onClose: () => any;
  defaultContent: string;
}

export default function AskAiDialog(props: AskAiDialogProps) {
  const { t } = useTranslation("AskAiDialog");
  const [aiResponseText, setAiResponseText] = useState("");
  const [openAskAIResponseDialog, setOpenAskAIResponseDialog] = useState(false);
  const [prompt, setPrompt] = useState("");

  useEffect(() => {
    if (!props.open) return;
    setPrompt(props.defaultContent);
  }, [props.open]);

  const { mutate: handleAskAi, isPending: isAIResponseDialogLoading } =
    useMutation({
      mutationFn: async (prompt: string) => {
        setOpenAskAIResponseDialog(true);
        const response = await OpenAiService.createAskPrompt({
          createAskAiBody: {
            message: prompt,
          },
        });

        return response.data.data;
      },
      onSuccess: (data) => {
        setAiResponseText(data || "");
      },
    });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleAskAi(prompt);
  };

  return (
    <>
      <Dialog
        open={props.open}
        onClose={props.onClose}
        PaperProps={{
          component: "form",
          onSubmit: handleSubmit,
        }}
      >
        <AskAiResponseDialog
          open={openAskAIResponseDialog}
          onClose={() => setOpenAskAIResponseDialog(false)}
          isDialogLoading={isAIResponseDialogLoading}
          response={aiResponseText}
        />
        <DialogTitle>{t("AskAI")}</DialogTitle>
        <IconButton
          aria-label="close"
          onClick={props.onClose}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,
            color: theme.palette.grey[500],
          })}
        >
          <CloseIcon />
        </IconButton>
        <DialogContent>
          <DialogContentText>{t("Description")}</DialogContentText>
          <TextField
            autoFocus
            required
            margin="dense"
            label={t("PromptLabel")}
            type="text"
            fullWidth
            variant="outlined"
            multiline
            minRows={4}
            defaultValue={props.defaultContent}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={props.onClose}>{t("Cancel")}</Button>
          <Button type="submit">{t("AskAI")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
